import React, { useState, useEffect } from 'react';
import EditableSection from 'components/v2/EditableSection';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import Label from 'components/v2/forms/Label';
import TextField from 'components/v2/forms/TextField';
import { titleize } from 'underscore.string';
import { useTranslation } from 'react-i18next';
import ROLES from 'constants/UserRoles';
import SessionStore from 'stores/SessionStore';
import { checkPermissonForEditShipperBillingModule } from './utils';
const BillingInfoSummary = ({ fields }) => {
    const { t } = useTranslation();
    const getFormattedAddress = (data = {}) => {
        let address = '';
        if (data.city) {
            address += data.city + ',';
        }
        if (data.state) {
            address += ' ' + data.state + ' ';
        }
        if (data.zipcode) {
            address += ' ' + data.zipcode;
        }
        return address;
    };
    return (React.createElement("div", { className: "grid grid-cols-2 gap-4" },
        React.createElement(DescriptionListItem, { label: t('NAME'), className: "col-span-full" }, fields.name),
        React.createElement(DescriptionListItem, { label: t('EMAIL'), className: "col-span-full" }, fields.email),
        React.createElement(DescriptionListItem, { label: t('ADDRESS'), className: "col-span-full" },
            fields.street && React.createElement("p", null, fields.street),
            getFormattedAddress(fields))));
};
const BillingInfoForm = ({ fields, onChange, saving, onSaveShipper, onSaveSuccess, onSaveFail, }) => {
    const { t } = useTranslation();
    const { name, email, street, city, state, zipcode } = fields;
    const saveForm = async () => {
        try {
            await onSaveShipper();
            onSaveSuccess(fields);
        }
        catch (e) {
            onSaveFail();
        }
    };
    useEffect(() => {
        if (saving) {
            saveForm();
        }
    }, [saving]);
    return (React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-3 gap-4" },
        React.createElement("div", { className: "col-span-full" },
            React.createElement(Label, null, t('NAME')),
            React.createElement(TextField, { className: "mt-1", value: name, onChange: (val) => onChange('name', val) })),
        React.createElement("div", { className: "col-span-full" },
            React.createElement(Label, null, t('EMAIL')),
            React.createElement(TextField, { type: "email", value: email, onChange: (val) => onChange('email', val), className: "mt-1" })),
        React.createElement("div", { className: "col-span-full" },
            React.createElement(Label, null, t('STREET_ADDRESS_1')),
            React.createElement(TextField, { type: "text", value: street, className: "mt-1", onChange: (val) => onChange('street', val) })),
        React.createElement("div", { className: "" },
            React.createElement(Label, null, titleize(t('CITY'))),
            React.createElement(TextField, { type: "text", value: city, className: "mt-1", onChange: (val) => onChange('city', val) })),
        React.createElement("div", { className: "" },
            React.createElement(Label, null, titleize(t('STATE'))),
            React.createElement(TextField, { type: "text", value: state, className: "mt-1", onChange: (val) => onChange('state', val) })),
        React.createElement("div", { className: "" },
            React.createElement(Label, null, t('ZIP')),
            React.createElement(TextField, { type: "number", value: zipcode, className: "mt-1", onChange: (val) => onChange('zipcode', val) }))));
};
const BillingInfo = ({ data, className, onUpdateShipper }) => {
    const { t } = useTranslation();
    const [initialData, setinitialData] = useState(data);
    const [billingInfoData, setBillingInfoData] = useState(data);
    const getInitData = () => {
        if (Object.keys(data)) {
            setBillingInfoData(data);
            setinitialData(data);
        }
    };
    const checkPermissonForEditBillingAddress = () => {
        const logedInUser = SessionStore.getUser();
        /**
         * if user type is Shipper or Admin with role finance-admin or role shipper-onboarding.
         * He can edit billing address else not.
         */
        if (logedInUser._type === ROLES.SHIPPER ||
            logedInUser.roles.includes(ROLES.FINANCE_ADMIN) ||
            logedInUser.roles.includes(ROLES.SHIPPER_ONBOARDING)) {
            return true;
        }
        return false;
    };
    const handleSave = async () => {
        if (!Object.keys(billingInfoData).every((key) => initialData[key] === billingInfoData[key])) {
            await onUpdateShipper({
                billing_address: Object.assign(Object.assign({}, billingInfoData), { isDisabled: checkPermissonForEditBillingAddress() }),
            });
        }
    };
    const handleAferSave = (onSaveSuccess) => {
        return (updatedData = {}) => {
            setinitialData((prev) => (Object.assign(Object.assign({}, prev), updatedData)));
            onSaveSuccess();
        };
    };
    useEffect(() => {
        getInitData();
    }, [data]);
    const onChange = (field, value) => {
        setBillingInfoData((prev) => (Object.assign(Object.assign({}, prev), { [field]: value })));
    };
    return (React.createElement(EditableSection, { className: className, title: t('BILLING_INFORMATION'), canEdit: checkPermissonForEditShipperBillingModule() }, (editMode, saving, onSaveSuccess, onSaveFail) => editMode ? (React.createElement(BillingInfoForm, { fields: billingInfoData, onChange: onChange, saving: saving, onSaveSuccess: handleAferSave(onSaveSuccess), onSaveFail: onSaveFail, onSaveShipper: () => handleSave() })) : (React.createElement(BillingInfoSummary, { fields: initialData }))));
};
export default BillingInfo;
